import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../environments/environment';
import { GeolocationService } from '../services/geolocation.service';
import { AuthenticationService } from '../services/loginServices/authentication.service';
import { EncryptionService } from '../services/loginServices/encryption.service';
import { ConfigService } from '../services/loginServices/configservice.service';
import { from, Observable, Subscription, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { env } from 'process';

@Component({
  selector: 'app-password-management',
  templateUrl: './password-management.component.html',
  styleUrls: ['./password-management.component.less']
})
export class PasswordManagementComponent implements OnInit {
  resetPasswordForm!: FormGroup;
  updatePasswordForm!: FormGroup;
  isResetMode: boolean = true;
  isLoading: boolean = false;
  showResetPassword: boolean = false;
  showUpdatePassword: boolean = false;
  
  // Password policy flags
  isFirstLogin: boolean = false;
  isPasswordExpired: boolean = false;
  passwordExpiryDays: number = 0;
  mustChangePassword: boolean = false;

  // Location data (you might want to get this from a geolocation service)
  private locationData = {
    gps_address: "Current Location Address", // Replace with actual geolocation service
    latitude: "0.0", // Replace with actual latitude
    longitude: "0.0" // Replace with actual longitude
  };

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private toastr: ToastrService,
    private geolocationService: GeolocationService,
    private authService: AuthenticationService,
    private encryptionService: EncryptionService,
    private configService: ConfigService
  ) {}

  private authSub?: Subscription;

  private getKrakendUrl(): string {
    const krakendUrl = this.configService.getKrakendUrl();
    if (!krakendUrl) {
      throw new Error("Krakend URL is not available.");
    }
    return krakendUrl;
  }

  ngOnInit(): void {
    this.initializeForms();
    this.getCurrentLocation();
    this.checkPasswordPolicy();

    // Subscribe to login data updates so that this component can react
    // when `is_first_login` and `password_changed_on` are saved after login
    this.authSub = this.authService.loginDataUpdated.subscribe(() => {
      this.checkPasswordPolicy();
    });
  }

  ngOnDestroy(): void {
    if (this.authSub) {
      this.authSub.unsubscribe();
    }
  }

  private checkPasswordPolicy(): void {
    // Get stored login data
    const isFirstLogin = localStorage.getItem('is_first_login');
    const passwordChangedOn = localStorage.getItem('password_changed_on');
    
    // Check if it's first login
    this.isFirstLogin = isFirstLogin === '1' || isFirstLogin === 'true';
    
    // Check password expiry (90 days = 3 months)
    if (passwordChangedOn) {
      const lastChanged = new Date(passwordChangedOn);
      const now = new Date();
      const daysDiff = Math.floor((now.getTime() - lastChanged.getTime()) / (1000 * 60 * 60 * 24));
      this.passwordExpiryDays = 90 - daysDiff;
      this.isPasswordExpired = daysDiff >= 90;
    }
    
    // Determine if password change is mandatory
    this.mustChangePassword = this.isFirstLogin || this.isPasswordExpired;
    
    // Auto-show update password form if mandatory
    if (this.mustChangePassword) {
      this.showUpdatePassword = true;
      
      if (this.isFirstLogin) {
        this.toastr.warning('First login detected. Please change your password to continue.', 'Password Change Required');
      } else if (this.isPasswordExpired) {
        this.toastr.warning('Your password has expired. Please change your password to continue.', 'Password Expired');
      }
    } else if (this.passwordExpiryDays <= 7 && this.passwordExpiryDays > 0) {
      this.toastr.info(`Your password will expire in ${this.passwordExpiryDays} days. Consider changing it soon.`, 'Password Expiry Warning');
    }
  }

  private initializeForms(): void {
    // Reset Password Form
    this.resetPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      username: ['', [Validators.required]]
    });

    // Update Password Form
    this.updatePasswordForm = this.fb.group({
      username: ['', [Validators.required]],
      current_password: ['', [Validators.required, Validators.minLength(6)]],
      updated_password: ['', [Validators.required, Validators.minLength(6), this.passwordStrengthValidator.bind(this)]],
      confirm_updated_password: ['', [Validators.required]]
    }, { validators: [this.passwordMatchValidator, this.passwordNotSameValidator] });
  }

  private passwordMatchValidator(form: FormGroup) {
    const updatedPassword = form.get('updated_password');
    const confirmPassword = form.get('confirm_updated_password');
    
    if (updatedPassword && confirmPassword && updatedPassword.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }
    return null;
  }

  private passwordNotSameValidator(form: FormGroup) {
    const currentPassword = form.get('current_password');
    const newPassword = form.get('updated_password');
    
    if (currentPassword && newPassword && 
        currentPassword.value && newPassword.value &&
        currentPassword.value === newPassword.value) {
      newPassword.setErrors({ sameAsOld: true });
      return { sameAsOld: true };
    }
    return null;
  }

  private passwordStrengthValidator(control: any) {
    const password = control.value;
    if (!password) return null;

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasMinLength = password.length >= 8;

    const errors: any = {};
    
    if (!hasUpperCase) errors.noUpperCase = true;
    if (!hasLowerCase) errors.noLowerCase = true;
    if (!hasNumber) errors.noNumber = true;
    if (!hasSpecialChar) errors.noSpecialChar = true;
    if (!hasMinLength) errors.minLength = true;

    return Object.keys(errors).length > 0 ? errors : null;
  }

  private getCurrentLocation(): void {
    this.geolocationService.getCurrentPosition()
      .then(location => {
        this.locationData = {
          gps_address: location.address,
          latitude: location.latitude,
          longitude: location.longitude
        };
        console.log('Location obtained:', this.locationData);
      })
      .catch(error => {
        console.warn('Could not get location:', error);
        // Keep default values
      });
  }

  toggleResetPassword(): void {
    this.showResetPassword = !this.showResetPassword;
    this.showUpdatePassword = false;
    if (this.showResetPassword) {
      this.resetPasswordForm.reset();
    }
  }

  toggleUpdatePassword(): void {
    // Don't allow closing if password change is mandatory
    if (this.mustChangePassword) {
      this.toastr.warning('Password change is mandatory. Please update your password to continue.', 'Required Action');
      return;
    }
    
    this.showUpdatePassword = !this.showUpdatePassword;
    this.showResetPassword = false;
    if (this.showUpdatePassword) {
      this.updatePasswordForm.reset();
    }
  }

  onResetPassword(): void {
    if (this.resetPasswordForm.valid && !this.isLoading) {
      this.isLoading = true;
      
      // Create the data object
      const data = {
        ...this.resetPasswordForm.value,
        ...this.locationData
      };

      const url = this.getKrakendUrl();
      if (url) {
        // Encrypt the data using the encryption service
        from(this.encryptionService.encryptParameters({ value: JSON.stringify(data) }))
          .pipe(
            switchMap((encryptedData) => {
              // Prepare the API request with the encrypted data
              return this.http.post(environment.php_encryption_url + "/library/php/resetPassword.php", { encrypted_data: encryptedData.value });
            }),
            catchError((error) => {
              console.error('Encryption failed:', error);
              // Handle the error properly
              return throwError(error);
            })
          ).subscribe({
            next: (response: any) => {
              this.isLoading = false;
              console.log('Reset password response:', response);
              
              // Check for success - status should be 1 for success in resetPassword.php
              if (response.status === 1 || response.status === '1') {
                this.toastr.success(response.message || 'Password reset request sent successfully!', 'Success');
                this.resetPasswordForm.reset();
                this.showResetPassword = false;
              } else {
                // Handle failure cases - status 0 or other failure indicators
                console.log('Password reset failed:', response);
                const errorMessage = response.message || 'Password reset failed!';
                
                // Check for specific error types
                if (errorMessage.toLowerCase().includes('username does not match with email')) {
                  this.toastr.error('Username does not match with the provided email address.', 'Mismatch Error');
                  this.resetEmailControl?.setErrors({ emailMismatch: true });
                } else if (errorMessage.toLowerCase().includes('account is blocked')) {
                  this.toastr.error('Your account is blocked. Cannot reset password.', 'Account Blocked');
                } else if (errorMessage.toLowerCase().includes('no data found')) {
                  this.toastr.error('Username not found in the system.', 'User Not Found');
                  this.resetUsernameControl?.setErrors({ userNotFound: true });
                } else if (errorMessage.toLowerCase().includes('cannot be blank')) {
                  this.toastr.error('Please fill in all required fields.', 'Missing Information');
                } else {
                  this.toastr.error(errorMessage, 'Reset Failed');
                }
              }
            },
            error: (error: any) => {
              this.isLoading = false;
              console.error('Reset password error:', error);
              
              // Check for different types of errors
              if (error.status === 0) {
                this.toastr.error('Network error or CORS issue. Please check your internet connection.', 'Connection Error');
              } else if (error.status === 401) {
                this.toastr.error('Authentication failed. Please login again.', 'Auth Error');
              } else if (error.status === 403) {
                this.toastr.error('Access forbidden. You don\'t have permission.', 'Permission Error');
              } else if (error.status >= 500) {
                this.toastr.error('Server error. Please try again later.', 'Server Error');
              } else {
                this.toastr.error(error.message || 'Failed to reset password. Please try again.', 'Error');
              }
            }
          });
      } else {
        // Use the original API request without encryption
        const apiUrl = `${environment.php_url}/android/library/php/resetPassword.php`;
        const headers = { 'skip-authorization': 'true' };

        this.http.post(apiUrl, data, { headers }).subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('Reset password response:', response);
            
            // Check for success - status should be 1 for success in resetPassword.php
            if (response.status === 1 || response.status === '1') {
              this.toastr.success(response.message || 'Password reset request sent successfully!', 'Success');
              this.resetPasswordForm.reset();
              this.showResetPassword = false;
            } else {
              // Handle failure cases - status 0 or other failure indicators
              console.log('Password reset failed:', response);
              const errorMessage = response.message || 'Password reset failed!';
              
              // Check for specific error types
              if (errorMessage.toLowerCase().includes('username does not match with email')) {
                this.toastr.error('Username does not match with the provided email address.', 'Mismatch Error');
                this.resetEmailControl?.setErrors({ emailMismatch: true });
              } else if (errorMessage.toLowerCase().includes('account is blocked')) {
                this.toastr.error('Your account is blocked. Cannot reset password.', 'Account Blocked');
              } else if (errorMessage.toLowerCase().includes('no data found')) {
                this.toastr.error('Username not found in the system.', 'User Not Found');
                this.resetUsernameControl?.setErrors({ userNotFound: true });
              } else if (errorMessage.toLowerCase().includes('cannot be blank')) {
                this.toastr.error('Please fill in all required fields.', 'Missing Information');
              } else {
                this.toastr.error(errorMessage, 'Reset Failed');
              }
            }
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('Reset password error:', error);
            
            // Check for different types of errors
            if (error.status === 0) {
              this.toastr.error('Network error or CORS issue. Please check your internet connection.', 'Connection Error');
            } else if (error.status === 401) {
              this.toastr.error('Authentication failed. Please login again.', 'Auth Error');
            } else if (error.status === 403) {
              this.toastr.error('Access forbidden. You don\'t have permission.', 'Permission Error');
            } else if (error.status >= 500) {
              this.toastr.error('Server error. Please try again later.', 'Server Error');
            } else {
              this.toastr.error(error.message || 'Failed to reset password. Please try again.', 'Error');
            }
          }
        });
      }
    } else {
      this.markFormGroupTouched(this.resetPasswordForm);
    }
  }

  onUpdatePassword(): void {
    console.log('=== onUpdatePassword called ===');
    console.log('Form valid:', this.updatePasswordForm.valid);
    console.log('Is loading:', this.isLoading);
    
    if (this.updatePasswordForm.valid && !this.isLoading) {
      this.isLoading = true;
      console.log('Starting password update process...');
      
      // Create the data object
      const data = {
        ...this.updatePasswordForm.value,
        ...this.locationData
      };
      console.log('Data to send:', data);

      const url = this.getKrakendUrl();
      if (url) {
        console.log('Using KrakenD URL with encryption:', url);
        // Encrypt the data using the encryption service
        from(this.encryptionService.encryptParameters({ value: JSON.stringify(data) }))
          .pipe(
            switchMap((encryptedData) => {
              console.log('Data encrypted successfully');
              // Prepare the API request with the encrypted data
              return this.http.post(environment.php_encryption_url + "/library/php/updateUserPassword.php", 
                { encrypted_data: encryptedData.value },
                { observe: 'response' }
              );
            }),
            catchError((error) => {
              console.error('Encryption failed:', error);
              // Handle the error properly
              return throwError(error);
            })
          ).subscribe({
            next: (response: any) => {
              console.log('=== HTTP Response received ===');
              this.isLoading = false;
              console.log('Full response object:', response);
              console.log('Response body:', response.body);
              
              const responseBody = response.body || response;
              console.log('Update password response:', responseBody);
              
              // Check for success - status should be 1 for success in updateUserPassword.php
              if (responseBody.status === 1 || responseBody.status === '1') {
                this.toastr.success(responseBody.message || 'Password updated successfully!', 'Success');
                
                // Update localStorage to reflect password change
                localStorage.setItem('is_first_login', '0');
                localStorage.setItem('password_changed_on', new Date().toISOString());
                
                // Reset policy flags
                this.isFirstLogin = false;
                this.isPasswordExpired = false;
                this.mustChangePassword = false;
                this.passwordExpiryDays = 90;
                
                this.updatePasswordForm.reset();
                this.showUpdatePassword = false;
                
                // If this was a mandatory change, show success message
                if (this.mustChangePassword) {
                  this.toastr.success('Password policy requirements satisfied. You can now continue using the application.', 'Policy Compliance');
                }
              } else {
                // Handle failure cases - status 0 or other failure indicators
                console.log('Password update failed:', responseBody);
                const errorMessage = responseBody.message || 'Password update failed!';
                
                // Check for specific error types
                if (errorMessage.toLowerCase().includes('current password does not match')) {
                  this.toastr.error('The current password you entered is incorrect. Please check and try again.', 'Wrong Password');
                  this.currentPasswordControl?.setErrors({ incorrectPassword: true });
                } else if (errorMessage.toLowerCase().includes('cannot be same as last 3 password')) {
                  this.toastr.error('New password cannot be same as your last 3 passwords. Please choose a different password.', 'Password History Violation');
                  this.updatedPasswordControl?.setErrors({ recentPassword: true });
                } else if (errorMessage.toLowerCase().includes('username')) {
                  this.toastr.error('Username not found or invalid. Please check your username.', 'Invalid Username');
                  this.updateUsernameControl?.setErrors({ invalidUsername: true });
                } else {
                  this.toastr.error(errorMessage, 'Update Failed');
                }
              }
            },
            error: (error: any) => {
              console.log('=== HTTP Error occurred ===');
              this.isLoading = false;
              console.error('Update password error:', error);
              
              // Check for different types of errors
              if (error.status === 0) {
                this.toastr.error('Network error or CORS issue. Please check your internet connection.', 'Connection Error');
              } else if (error.status === 401) {
                this.toastr.error('Authentication failed. Please login again.', 'Auth Error');
              } else if (error.status === 403) {
                this.toastr.error('Access forbidden. You don\'t have permission.', 'Permission Error');
              } else if (error.status >= 500) {
                this.toastr.error('Server error. Please try again later.', 'Server Error');
              } else {
                this.toastr.error(error.message || 'Failed to update password. Please try again.', 'Error');
              }
            }
          });
      } else {
        console.log('Using original API without encryption');
        // Use the original API request without encryption
        const apiUrl = `${environment.php_url}/android/library/php/updateUserPassword.php`;
        console.log('API URL (using proxy):', apiUrl);

        const headers = new HttpHeaders({
          'skip-authorization': 'true',
          'Content-Type': 'application/json'
        });
        
        this.http.post(apiUrl, data, { 
          headers,
          observe: 'response'
        }).subscribe({
          next: (response: any) => {
            console.log('=== HTTP Response received ===');
            this.isLoading = false;
            console.log('Full response object:', response);
            console.log('Response body:', response.body);
            
            const responseBody = response.body || response;
            console.log('Update password response:', responseBody);
            
            // Check for success - status should be 1 for success in updateUserPassword.php
            if (responseBody.status === 1 || responseBody.status === '1') {
              this.toastr.success(responseBody.message || 'Password updated successfully!', 'Success');
              
              // Update localStorage to reflect password change
              localStorage.setItem('is_first_login', '0');
              localStorage.setItem('password_changed_on', new Date().toISOString());
              
              // Reset policy flags
              this.isFirstLogin = false;
              this.isPasswordExpired = false;
              this.mustChangePassword = false;
              this.passwordExpiryDays = 90;
              
              this.updatePasswordForm.reset();
              this.showUpdatePassword = false;
              
              // If this was a mandatory change, show success message
              if (this.mustChangePassword) {
                this.toastr.success('Password policy requirements satisfied. You can now continue using the application.', 'Policy Compliance');
              }
            } else {
              // Handle failure cases - status 0 or other failure indicators
              console.log('Password update failed:', responseBody);
              const errorMessage = responseBody.message || 'Password update failed!';
              
              // Check for specific error types
              if (errorMessage.toLowerCase().includes('current password does not match')) {
                this.toastr.error('The current password you entered is incorrect. Please check and try again.', 'Wrong Password');
                this.currentPasswordControl?.setErrors({ incorrectPassword: true });
              } else if (errorMessage.toLowerCase().includes('cannot be same as last 3 password')) {
                this.toastr.error('New password cannot be same as your last 3 passwords. Please choose a different password.', 'Password History Violation');
                this.updatedPasswordControl?.setErrors({ recentPassword: true });
              } else if (errorMessage.toLowerCase().includes('username')) {
                this.toastr.error('Username not found or invalid. Please check your username.', 'Invalid Username');
                this.updateUsernameControl?.setErrors({ invalidUsername: true });
              } else {
                this.toastr.error(errorMessage, 'Update Failed');
              }
            }
          },
          error: (error: any) => {
            console.log('=== HTTP Error occurred ===');
            this.isLoading = false;
            console.error('Update password error:', error);
            
            // Check for different types of errors
            if (error.status === 0) {
              this.toastr.error('Network error or CORS issue. Please check your internet connection.', 'Connection Error');
            } else if (error.status === 401) {
              this.toastr.error('Authentication failed. Please login again.', 'Auth Error');
            } else if (error.status === 403) {
              this.toastr.error('Access forbidden. You don\'t have permission.', 'Permission Error');
            } else if (error.status >= 500) {
              this.toastr.error('Server error. Please try again later.', 'Server Error');
            } else {
              this.toastr.error(error.message || 'Failed to update password. Please try again.', 'Error');
            }
          }
        });
      }
    } else {
      console.log('=== Form validation failed ===');
      console.log('Form valid:', this.updatePasswordForm.valid);
      console.log('Form errors:', this.updatePasswordForm.errors);
      console.log('Form controls:');
      Object.keys(this.updatePasswordForm.controls).forEach(key => {
        const control = this.updatePasswordForm.get(key);
        console.log(`${key}:`, {
          value: control?.value,
          valid: control?.valid,
          errors: control?.errors
        });
      });
      this.markFormGroupTouched(this.updatePasswordForm);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  // Getter methods for easy access in template
  get resetEmailControl() { return this.resetPasswordForm.get('email'); }
  get resetUsernameControl() { return this.resetPasswordForm.get('username'); }
  get updateUsernameControl() { return this.updatePasswordForm.get('username'); }
  get currentPasswordControl() { return this.updatePasswordForm.get('current_password'); }
  get updatedPasswordControl() { return this.updatePasswordForm.get('updated_password'); }
  get confirmPasswordControl() { return this.updatePasswordForm.get('confirm_updated_password'); }

  // Helper methods for password policy display
  get passwordPolicyMessage(): string {
    if (this.isFirstLogin) {
      return 'First login detected - Password change is mandatory';
    } else if (this.isPasswordExpired) {
      return 'Password has expired - Change required';
    } else if (this.passwordExpiryDays <= 7 && this.passwordExpiryDays > 0) {
      return `Password expires in ${this.passwordExpiryDays} days`;
    }
    return '';
  }

  get passwordStrengthErrors(): string[] {
    const control = this.updatedPasswordControl;
    const errors: string[] = [];
    
    if (control?.errors) {
      if (control.errors['noUpperCase']) errors.push('At least one uppercase letter');
      if (control.errors['noLowerCase']) errors.push('At least one lowercase letter');
      if (control.errors['noNumber']) errors.push('At least one number');
      if (control.errors['noSpecialChar']) errors.push('At least one special character');
      if (control.errors['minLength']) errors.push('Minimum 8 characters');
      if (control.errors['sameAsOld']) errors.push('Must be different from current password');
    }
    
    return errors;
  }
}
